import type { Invitation } from "../../types/invitations";
import { formatUserDate, roleLabel } from "./userDisplay";
import "../../components/DataTable.css";
import "./PendingInvitations.css";

interface PendingInvitationsProps {
  invitations: Invitation[];
  /** Turns the issuing admin's id into a name. The id is all the API carries. */
  invitedByName: (userId: string | null) => string;
  /** Every invitation with an action in flight. A set, because two rows can be working at once. */
  busyIds: ReadonlySet<string>;
  onResend: (invitation: Invitation) => void;
  onRevoke: (invitation: Invitation) => void;
}

/** A date that may be missing because an older API did not send it. */
const dateOrUnknown = (iso: string | null): string =>
  iso ? formatUserDate(iso) : "Unknown";

/**
 * Invitations that have been sent and not yet used.
 *
 * The same table chrome as the users list above it, because it answers the same question about the
 * same people, one step earlier. An invitation is not an account, so it is a separate table rather
 * than greyed-out rows among real users.
 *
 * **Nothing renders when there are none.** An empty table on a screen that mostly has none is a
 * permanent reminder of a state the admin is not in.
 */
export const PendingInvitations = ({
  invitations,
  invitedByName,
  busyIds,
  onResend,
  onRevoke,
}: PendingInvitationsProps) => {
  if (invitations.length === 0) return null;

  return (
    <section className="pending-invitations data-table-card">
      <header className="data-table-card__header">
        <h2 className="data-table-card__title">Pending invitations</h2>
        <p className="data-table-card__subtitle">
          {invitations.length}{" "}
          {invitations.length === 1 ? "person has" : "people have"} been invited
          and have not signed up yet. Their account appears in the users list
          once they do.
        </p>
      </header>

      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Invited by</th>
              <th scope="col">Sent</th>
              <th scope="col">Expires</th>
              <th scope="col" className="data-table__actions">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((invitation) => {
              const busy = busyIds.has(invitation.id);

              return (
                <tr key={invitation.id}>
                  <td>
                    <div className="data-table__identity">
                      <span className="data-table__avatar" aria-hidden="true">
                        {invitation.email[0]?.toUpperCase() ?? "?"}
                      </span>
                      <span className="pending-invitations__email">
                        {invitation.email}
                      </span>
                      {/*
                        Only when the server actually said the send failed. An older API that does
                        not report delivery sends null, and a null is not evidence of anything.
                      */}
                      {invitation.lastSendSucceeded === false && (
                        <span className="data-table__tag pending-invitations__tag--warning">
                          Not delivered
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`data-table__badge${
                        invitation.role === "user"
                          ? ""
                          : " data-table__badge--primary"
                      }`}
                    >
                      {roleLabel(invitation.role)}
                    </span>
                  </td>
                  <td className="data-table__muted">
                    {invitedByName(invitation.createdBy)}
                  </td>
                  <td className="data-table__muted">
                    {dateOrUnknown(invitation.lastSentAt ?? invitation.createdAt)}
                    {invitation.sentCount !== null && invitation.sentCount > 1
                      ? ` (sent ${invitation.sentCount} times)`
                      : ""}
                  </td>
                  <td className="data-table__muted">
                    {dateOrUnknown(invitation.expiresAt)}
                  </td>
                  <td className="data-table__actions">
                    <div className="pending-invitations__actions">
                      {/*
                        Three things about these two buttons, all of which have bitten this
                        codebase before:

                        Each is named after the person it acts on. "Resend" repeated down a column
                        tells a screen reader user nothing about which row they are on, and these
                        are not actions to get wrong.

                        `aria-disabled`, not `disabled`. A disabled button leaves the tab order the
                        instant it is activated, so a keyboard user's focus falls to <body> and
                        never comes back when the row settles. TrustedContactRow does the same, for
                        the same reason. The handler stays attached and returns early instead.

                        The visible label does not change while working, because the accessible
                        name does not either: swapping "Resend" for "Working…" under a fixed
                        aria-label leaves the two disagreeing, which is WCAG 2.5.3. aria-busy is
                        what says the work is happening.
                      */}
                      <button
                        type="button"
                        className="pending-invitations__action"
                        data-testid={`invitation-resend-${invitation.id}`}
                        aria-disabled={busy}
                        aria-busy={busy}
                        aria-label={`Resend the invitation to ${invitation.email}`}
                        onClick={() => {
                          if (busy) return;
                          onResend(invitation);
                        }}
                      >
                        Resend
                      </button>
                      <button
                        type="button"
                        className="pending-invitations__action pending-invitations__action--danger"
                        data-testid={`invitation-withdraw-${invitation.id}`}
                        aria-disabled={busy}
                        aria-busy={busy}
                        aria-label={`Withdraw the invitation to ${invitation.email}`}
                        onClick={() => {
                          if (busy) return;
                          onRevoke(invitation);
                        }}
                      >
                        Withdraw
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
